import React, { useRef, useCallback, useEffect } from 'react';

const TOOLBAR_BUTTONS = [
  { cmd: 'bold', label: 'B', style: 'font-bold' },
  { cmd: 'italic', label: 'I', style: 'italic' },
  { cmd: 'underline', label: 'U', style: 'underline' },
  { cmd: 'strikeThrough', label: 'S', style: 'line-through' },
  { type: 'separator' },
  { cmd: 'justifyLeft', label: 'Left', icon: 'align-left' },
  { cmd: 'justifyCenter', label: 'Center', icon: 'align-center' },
  { cmd: 'justifyRight', label: 'Right', icon: 'align-right' },
  { type: 'separator' },
  { cmd: 'insertUnorderedList', label: 'List', icon: 'list' },
  { cmd: 'insertOrderedList', label: '1.', icon: 'list-ordered' },
  { type: 'separator' },
  { cmd: 'formatBlock', label: 'H1', arg: 'h1' },
  { cmd: 'formatBlock', label: 'H2', arg: 'h2' },
  { cmd: 'formatBlock', label: 'P', arg: 'p' },
  { type: 'separator' },
  { cmd: 'createLink', label: 'Link', icon: 'link', prompt: true },
  { cmd: 'removeFormat', label: 'Clear', icon: 'clear' },
];

export function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      const html = editorRef.current.innerHTML;
      onChange(html === '<br>' ? '' : html);
    }
  }, [onChange]);

  const execCommand = useCallback((cmd, arg) => {
    if (cmd === 'createLink') {
      const url = prompt('Enter URL:', 'https://');
      if (url) {
        document.execCommand(cmd, false, url);
      }
    } else {
      document.execCommand(cmd, false, arg || null);
    }
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const handleColorChange = useCallback((e) => {
    document.execCommand('foreColor', false, e.target.value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b">
        {TOOLBAR_BUTTONS.map((btn, i) => {
          if (btn.type === 'separator') {
            return <div key={i} className="w-px h-5 bg-slate-200 mx-1" />;
          }
          return (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand(btn.cmd, btn.arg);
              }}
              className={`px-2 py-1 text-xs rounded hover:bg-slate-200 transition-colors ${btn.style || ''}`}
              title={btn.label}
            >
              {btn.label}
            </button>
          );
        })}
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <label className="flex items-center gap-1 px-1 cursor-pointer" title="Text Color">
          <span className="text-xs">A</span>
          <input
            type="color"
            defaultValue="#5E5E5E"
            onChange={handleColorChange}
            className="w-4 h-4 border-0 p-0 cursor-pointer"
          />
        </label>
      </div>

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        data-placeholder={placeholder || 'Start typing...'}
        className="min-h-[200px] p-4 text-sm outline-none prose prose-sm max-w-none [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-slate-400"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
      />
    </div>
  );
}
